import styled, { DefaultTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Spacer, VDBText } from "@components/atoms";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import React, { useMemo } from "react";
import RadioButton from "@components/atoms/RadioButton";
import { computePlanPrice } from "./utils";
import UserSelector from "./components/UserSelector";

export type CardAction = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
};

export type CardProps = {
  title: string;
  description?: string;
  children?: React.ReactNode;
  actions?: CardAction[];
  checked?: boolean;
  onSelect?: () => void;
  item?: any;
  additionalUsers: number;
  setAdditionalUsers: (value: number) => void;
  disabled?: boolean;
};

const Card: React.FC<CardProps> = ({
  title,
  description,
  checked,
  onSelect,
  item,
  additionalUsers,
  setAdditionalUsers,
  disabled,
}) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const totals = useMemo(
    () => computePlanPrice(item, additionalUsers),
    [item, additionalUsers]
  );

  const computePlanPriceComponent = (plan: any) => {
    const basePrice = plan?.base_price ?? 0;
    const discountPercent = plan?.discount_percent ?? 0;
    const discountedPrice = basePrice * (1 - discountPercent / 100);
    return discountedPrice;
  };

  return (
    <CardContainer checked={!!checked} onPress={onSelect} disabled={disabled}>
      <TopContainer checked={!!checked}>
        <HeaderRow>
          <RadioButtonContainer>
            <RadioButton
              checked={!!checked}
              onPress={onSelect ?? (() => {})}
              margin="0"
              hideLabel={true}
            />
            <Spacer size={SpaceType.Basic} />
            <VDBText
              type={typography["Lead-M"]}
              color={theme[checked ? "core-05" : "core-06"]}
            >
              {title}
            </VDBText>
          </RadioButtonContainer>
          {item.plan_info.map(
            (plan: any) =>
              plan?.discount_percent && (
                <DiscountContainer>
                  <VDBText
                    type={typography["Minor-M"]}
                    color={theme["success-01"]}
                  >
                    {t("managePlan.popUp.save.text", "Save ")}
                    {plan?.discount_percent}%
                  </VDBText>
                </DiscountContainer>
              )
          )}
        </HeaderRow>
        <Spacer size={SpaceType.ExtraSmall} />
        <VDBText type={typography["Minor-M"]} color={theme["success-01"]}>
          {description && !item.disablePromotionText ? description : " "}
        </VDBText>
      </TopContainer>
      <PlanInfoContainer>
        {item.plan_info.map((plan: any, index: number) => (
          <>
            <PriceContainer>
              <PlanInfoRow>
                {plan?.discount_percent && (
                  <>
                    <VDBText
                      type={typography["Body-R"]}
                      color={theme["error-01"]}
                      textDecoration="line-through"
                    >
                      {plan?.currency_symbol}
                      {plan?.base_price}
                    </VDBText>
                    <Spacer size={SpaceType.Small} />{" "}
                  </>
                )}
                <VDBText type={typography["H1-B"]} color={theme["core-05"]}>
                  {plan?.currency_symbol}
                  {computePlanPriceComponent(plan)}
                </VDBText>
                <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                  {t("managePlan.popUp.basePriceCard.text", " / base price")}
                </VDBText>
              </PlanInfoRow>

              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                {t("managePlan.popUp.includeUser.text", "Base plan includes")}{" "}
                {item?.base_included_users ?? 0}{" "}
                {t("managePlan.popUp.user.text", "user")}
              </VDBText>
            </PriceContainer>

            {index !== item.plan_info.length - 1 && (
              <Spacer size={SpaceType.Basic} />
            )}
          </>
        ))}
      </PlanInfoContainer>
      <BottomContainer>
        <UserSelector
          id={item.plan_id}
          pricePerAdditionalUser={totals?.addUsersCost ?? 0}
          value={checked ? additionalUsers : 0}
          onChange={setAdditionalUsers}
          item={item}
          disabled={!checked}
        />
      </BottomContainer>
    </CardContainer>
  );
};

const CardContainer = styled.Pressable<{ checked: boolean }>`
  background-color: ${({
    theme,
    checked,
  }: {
    theme: DefaultTheme;
    checked: boolean;
  }) => theme[checked ? "core-01" : "core-02"]};
  border-radius: ${SpaceType.Basic}px;
  border-width: 2px;
  border-color: ${({
    theme,
    checked,
  }: {
    theme: DefaultTheme;
    checked: boolean;
  }) => theme[checked ? "main-06" : "core-02"]};
`;

const HeaderRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const RadioButtonContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const TopContainer = styled.View<{ checked: boolean }>`
  padding: ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const BottomContainer = styled.View``;

const PlanInfoContainer = styled.View`
  padding: ${SpaceType.Extra}px;
  border-bottom-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const PriceContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PlanInfoRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const DiscountContainer = styled.View`
  padding-vertical: 3px;
  padding-horizontal: ${SpaceType.ExtraSmall}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["success-02"]};
  border-radius: ${SpaceType.ExtraSmall}px;
`;

export default Card;
